import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { PLAYLIST_INFO_ACTIONS } from '@soundxyz/utils/constants/logging';
import { Avatar } from '@components/basics/Images/Avatar';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { useAuth } from '@context/auth/base';
import { PlaylistCardSocialProofDocument, useQuery } from '@graphql/reactQuery';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import { MintsDrivenTrigger } from './MintsDrivenTrigger';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PlaylistCardSocialProof($playlistId: UUID!, $userPublicAddress: String) {
    playlistV2(id: $playlistId) {
      id
      numReferralPurchases
      socialProofReferrals(userPublicAddress: $userPublicAddress) {
        id
        username
        publicAddress
        webappUri
        avatar {
          id
          url
        }
      }
    }
  }
`);

const AVATAR_SIZE = 24;

export const PlaylistCardSocialProof = ({ playlistId }: { playlistId: string }) => {
  const { authUser } = useAuth();

  const { data, isLoading } = useQuery(PlaylistCardSocialProofDocument, {
    variables: {
      playlistId,
      userPublicAddress: authUser?.publicAddress,
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const playlist = data?.data.playlistV2;
  if (!playlist) {
    return (
      <div className="h-11 w-full px-3 pb-4 pt-1">
        {isLoading && <Skeleton className="h-5 w-40 rounded-md" />}
      </div>
    );
  }
  const { id, numReferralPurchases: mintsDriven, socialProofReferrals } = playlist;

  return (
    <div className="flex h-11 w-full items-center px-3 pb-4 pt-1 text-base-xs text-base300">
      <div
        className={clsx(
          'flex flex-row-reverse',
          (!!authUser || socialProofReferrals.length > 0) && 'mr-2',
        )}
      >
        {socialProofReferrals.length > 0 ? (
          // Reverse so they show up in the right stacking order without needing separate z-indices
          [...socialProofReferrals].reverse().map((user, index) => (
            <div className={clsx(index < socialProofReferrals.length - 1 && '-ml-3')} key={user.id}>
              <Avatar
                src={user.avatar?.url}
                size={AVATAR_SIZE}
                username={user.username}
                walletAddress={user.publicAddress}
                hasShadow
                hasBorder={false}
                hasDarkBorder
                borderRadius="50%"
                webappUri={user.webappUri}
              />
            </div>
          ))
        ) : !!authUser ? (
          <Avatar
            src={authUser.avatar?.url}
            size={AVATAR_SIZE}
            username={authUser.username}
            walletAddress={authUser.publicAddress}
            hasShadow
            hasBorder={false}
            hasDarkBorder
            borderRadius="50%"
            webappUri={authUser.webappUri}
          />
        ) : null}
      </div>
      {mintsDriven === 0 ? (
        <span>Mint a song from the playlist</span>
      ) : (
        <MintsDrivenTrigger
          className="transition-[filter] duration-300 hover:brightness-130"
          playlistId={id}
          mintsDriven={mintsDriven}
          infoAction={PLAYLIST_INFO_ACTIONS.PLAYLIST_CARD_MINTS_DRIVEN_CLICK}
        />
      )}
    </div>
  );
};
